import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLocale, formatDate } from "@/i18n/format";
import { useAuth } from "@/hooks/use-auth";
import { POST_TYPES, POST_TYPE_META } from "@/hooks/use-post-generation";
import {
  namedPromptKeys,
  promptTitle,
  requiredPlaceholders,
  useGlobalPrompts,
  useGlobalPromptVersions,
  useSaveGlobalPrompt,
  useRestoreGlobalPromptVersion,
  type GlobalPromptOverview,
  type PromptTemplate,
} from "@/hooks/use-prompts";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";

export function AdminPromptsPage() {
  const { t } = useTranslation("admin");
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  const { data: overview, isLoading: loadingPrompts } = useGlobalPrompts();
  const [editing, setEditing] = useState<string | null>(null);
  const [historyFor, setHistoryFor] = useState<string | null>(null);
  const [tier, setTier] = useState<"all" | 1 | 2 | 3>("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (isLoading) return;
    // Org-owner-only — matches the requireOrgOwner() middleware on /admin/prompts/global/*.
    if (!user?.isOrgOwner) navigate("/dashboard", { replace: true });
  }, [isLoading, user, navigate]);

  const filtered = useMemo(
    () =>
      POST_TYPES.filter((pt) => {
        const meta = POST_TYPE_META[pt];
        if (tier !== "all" && meta.tier !== tier) return false;
        if (
          search &&
          !pt.toLowerCase().includes(search.toLowerCase()) &&
          !meta.label.toLowerCase().includes(search.toLowerCase())
        )
          return false;
        return true;
      }),
    [tier, search],
  );
  // Named (non-PostType) prompts have no tier; list them under "All tiers".
  const filteredNamed = useMemo(
    () =>
      tier !== "all" || !overview
        ? []
        : namedPromptKeys(overview).filter((k) => {
            const label = overview[k]!.named!.label;
            return !search || `${k} ${label}`.toLowerCase().includes(search.toLowerCase());
          }),
    [overview, tier, search],
  );

  if (isLoading || loadingPrompts || !overview) {
    return (
      <div className="px-6 py-8 w-full space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  return (
    <div className="px-6 py-8 w-full space-y-6 max-w-6xl">
      <header>
        <h1 className="text-2xl font-semibold">{t("prompts.title")}</h1>
        <p className="text-sm text-muted-foreground">
          {t("prompts.subtitle")}
        </p>
      </header>

      <div className="flex items-center gap-3">
        <input
          type="text"
          placeholder={t("prompts.searchPlaceholder")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="rounded border px-3 py-2 text-sm flex-1 max-w-xs"
        />
        <select
          value={String(tier)}
          onChange={(e) => setTier(e.target.value === "all" ? "all" : (Number(e.target.value) as 1 | 2 | 3))}
          className="rounded border px-3 py-2 text-sm"
        >
          <option value="all">{t("prompts.tierFilter.all")}</option>
          <option value="1">{t("prompts.tierFilter.tier1")}</option>
          <option value="2">{t("prompts.tierFilter.tier2")}</option>
          <option value="3">{t("prompts.tierFilter.tier3")}</option>
        </select>
      </div>

      <section className="rounded-lg border divide-y">
        {filtered.map((pt) => {
          const o = overview[pt];
          if (!o) return null;
          return (
            <GlobalRow
              key={pt}
              postType={pt}
              overview={o}
              onEdit={() => setEditing(pt)}
              onHistory={() => setHistoryFor(pt)}
            />
          );
        })}
      </section>

      {filteredNamed.length > 0 && (
        <section className="space-y-2">
          <header>
            <h2 className="text-lg font-semibold">{t("prompts.otherTitle")}</h2>
            <p className="text-sm text-muted-foreground">{t("prompts.otherSubtitle")}</p>
          </header>
          <div className="rounded-lg border divide-y">
            {filteredNamed.map((k) => (
              <GlobalRow
                key={k}
                postType={k}
                overview={overview[k]!}
                onEdit={() => setEditing(k)}
                onHistory={() => setHistoryFor(k)}
              />
            ))}
          </div>
        </section>
      )}

      {editing && (
        <GlobalEditorModal
          postType={editing}
          overview={overview[editing]!}
          onClose={() => setEditing(null)}
        />
      )}
      {historyFor && (
        <GlobalHistoryModal
          postType={historyFor}
          onClose={() => setHistoryFor(null)}
        />
      )}
    </div>
  );
}

function GlobalRow({
  postType, overview, onEdit, onHistory,
}: {
  postType: string;
  overview: GlobalPromptOverview;
  onEdit: () => void;
  onHistory: () => void;
}) {
  const { t } = useTranslation("admin");
  const tpl = overview.template;
  const subtitle = tpl?.notes ?? overview.named?.description;
  return (
    <div className="flex items-center gap-3 p-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">{promptTitle(postType, overview.named)}</span>
          <span className="font-mono text-[11px] text-muted-foreground">{postType}</span>
          {overview.activeScope === "global"
            ? <span className="rounded bg-muted text-muted-foreground px-2 py-0.5 text-[10px] uppercase tracking-wider">{t("prompts.row.globalBadge", { version: tpl?.version })}</span>
            : <span className="rounded bg-amber-500/15 text-amber-400 px-2 py-0.5 text-[10px] uppercase tracking-wider">{t("prompts.row.bootstrapBadge")}</span>}
        </div>
        {subtitle && (
          <div className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{subtitle}</div>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Button size="sm" variant="outline" onClick={onEdit}>{t("common:actions.edit")}</Button>
        <Button size="sm" variant="ghost" onClick={onHistory}>{t("prompts.history.historyBtn")}</Button>
      </div>
    </div>
  );
}

function GlobalEditorModal({
  postType, overview, onClose,
}: {
  postType: string;
  overview: GlobalPromptOverview;
  onClose: () => void;
}) {
  const { t } = useTranslation("admin");
  const tpl = overview.template;
  const named = overview.named;
  // Named prompts start from their bootstrap so an edit isn't a rewrite.
  const [template, setTemplate] = useState(tpl?.template ?? named?.bootstrapTemplate ?? "");
  const [modelName, setModelName] = useState(tpl?.modelName ?? "");
  const [notes, setNotes] = useState("");
  const save = useSaveGlobalPrompt(postType);
  const missing = requiredPlaceholders(named).filter((p) => !template.includes(p));
  const hasBrief = missing.length === 0;
  const dirty = template !== tpl?.template;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-background border rounded-lg w-full max-w-3xl max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <header className="border-b p-4 flex items-center justify-between sticky top-0 bg-background">
          <div>
            <h2 className="text-lg font-semibold">{t("prompts.editor.globalPrefix")}{promptTitle(postType, named)}</h2>
            <p className="text-xs text-muted-foreground font-mono">{postType}</p>
          </div>
          <Button size="sm" variant="ghost" onClick={onClose}>{t("common:actions.close")}</Button>
        </header>
        <div className="p-4 space-y-3">
          <p className="text-xs text-amber-400">
            {t("prompts.editor.warning")}
          </p>
          <div>
            <label className="block text-xs font-medium uppercase tracking-wider mb-1">{t("prompts.editor.templateLabel")}</label>
            <textarea
              value={template}
              onChange={(e) => setTemplate(e.target.value)}
              rows={20}
              className="w-full rounded border bg-background p-3 font-mono text-xs"
              placeholder={named ? "" : t("prompts.editor.templatePlaceholder")}
            />
            {named && named.variables.length > 0 && (
              <p className="text-xs text-muted-foreground mt-1 font-mono">
                {t("prompts.editor.variablesHint", { vars: named.variables.map((v) => `{${v}}`).join(" ") })}
              </p>
            )}
            {!hasBrief && (
              <p className="text-xs text-red-400 mt-1">
                {named
                  ? t("prompts.editor.missingPlaceholders", { placeholders: missing.join(", ") })
                  : t("prompts.editor.briefRequired")}
              </p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium uppercase tracking-wider mb-1">{t("prompts.editor.modelLabel")}</label>
              <input
                type="text"
                value={modelName ?? ""}
                onChange={(e) => setModelName(e.target.value)}
                className="w-full rounded border px-2 py-1.5 text-sm"
                placeholder={t("prompts.editor.modelPlaceholder")}
              />
            </div>
            <div>
              <label className="block text-xs font-medium uppercase tracking-wider mb-1">{t("prompts.editor.notesLabel")}</label>
              <input
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full rounded border px-2 py-1.5 text-sm"
                placeholder={t("prompts.editor.notesPlaceholder")}
              />
            </div>
          </div>
        </div>
        <footer className="border-t p-4 flex items-center justify-end gap-2 sticky bottom-0 bg-background">
          <Button variant="ghost" onClick={onClose}>{t("common:actions.cancel")}</Button>
          <Button
            disabled={!hasBrief || !dirty || save.isPending}
            onClick={() => {
              save.mutate(
                { template, modelName: modelName || null, notes: notes || null },
                {
                  onSuccess: () => {
                    toast.success(t("prompts.editor.savedSuccess"));
                    onClose();
                  },
                  onError: (e) => toast.error((e as Error).message),
                },
              );
            }}
          >
            {save.isPending ? t("prompts.editor.saving") : t("prompts.editor.saveNewVersion")}
          </Button>
        </footer>
      </div>
    </div>
  );
}

function GlobalHistoryModal({
  postType, onClose,
}: { postType: string; onClose: () => void }) {
  const { t } = useTranslation("admin");
  const locale = useLocale();
  const { data: versions, isLoading } = useGlobalPromptVersions(postType);
  const restore = useRestoreGlobalPromptVersion(postType);
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-background border rounded-lg w-full max-w-3xl max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <header className="border-b p-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">{postType}{t("prompts.history.titleSuffix")}</h2>
          <Button size="sm" variant="ghost" onClick={onClose}>{t("common:actions.close")}</Button>
        </header>
        <div className="p-4">
          {isLoading ? <Skeleton className="h-32 w-full" /> : (
            (versions ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("prompts.history.noVersions")}</p>
            ) : (
              <ul className="space-y-2">
                {(versions ?? []).map((v: PromptTemplate) => (
                  <li key={v.id} className="rounded border p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm font-medium">v{v.version} {v.active && <span className="text-green-400">{t("prompts.history.active")}</span>} {v.archivedAt && <span className="text-muted-foreground">{t("prompts.history.archived")}</span>}</div>
                        <div className="text-xs text-muted-foreground">{formatDate(new Date(v.createdAt), locale, { dateStyle: "medium", timeStyle: "short" })}</div>
                        {v.notes && <div className="text-xs text-muted-foreground mt-1">{v.notes}</div>}
                      </div>
                      {!v.active && !v.archivedAt && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => restore.mutate(v.id, { onSuccess: () => toast.success(t("prompts.history.restoredSuccess", { version: v.version })) })}
                        >
                          {t("prompts.history.restore")}
                        </Button>
                      )}
                    </div>
                    <pre className="text-xs bg-muted/30 rounded p-2 mt-2 overflow-auto max-h-40 whitespace-pre-wrap">{v.template}</pre>
                  </li>
                ))}
              </ul>
            )
          )}
        </div>
      </div>
    </div>
  );
}
